import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, addMonths, subMonths, isBefore, startOfDay } from 'date-fns';

const Calendar = ({
  selectedDate,
  onDateSelect,
  minDate = new Date(),
  maxDate,
  className = '',
  ...props
}) => {
  const [currentMonth, setCurrentMonth] = useState(selectedDate || new Date());
  
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  // Get starting day of week for the month (0 = Sunday, 6 = Saturday)
  const startDay = monthStart.getDay();
  
  // Create array of empty cells for days before month starts
  const emptyCells = Array(startDay).fill(null);
  
  const isDateDisabled = (date) => {
    const today = startOfDay(new Date());
    const checkDate = startOfDay(date);
    
    if (isBefore(checkDate, today)) return true;
    if (minDate && isBefore(checkDate, startOfDay(minDate))) return true;
    if (maxDate && isBefore(startOfDay(maxDate), checkDate)) return true;
    
    return false;
  };

  const handleDateClick = (date) => {
    if (!isDateDisabled(date)) {
      onDateSelect(date);
    }
  };

  const nextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const prevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className={`bg-white p-6 rounded-xl shadow-lg ${className}`} {...props}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={prevMonth}
          className="p-2 rounded-full hover:bg-pragyavan-cream transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-pragyavan-brown" />
        </motion.button>
        
        <h2 className="text-lg font-semibold text-pragyavan-brown">
          {format(currentMonth, 'MMMM yyyy')}
        </h2>
        
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={nextMonth}
          className="p-2 rounded-full hover:bg-pragyavan-cream transition-colors"
        >
          <ChevronRight className="w-5 h-5 text-pragyavan-brown" />
        </motion.button>
      </div>

      {/* Day Names */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {dayNames.map((day) => (
          <div key={day} className="text-center text-sm font-medium text-warm-gray p-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {/* Empty cells for days before month starts */}
        {emptyCells.map((_, index) => (
          <div key={`empty-${index}`} className="p-2" />
        ))}
        
        {/* Days of the month */}
        {daysInMonth.map((date) => {
          const disabled = isDateDisabled(date);
          const selected = selectedDate && isSameDay(date, selectedDate);
          const today = isToday(date);
          
          return (
            <motion.button
              key={date.toString()}
              whileHover={disabled ? {} : { scale: 1.1 }}
              whileTap={disabled ? {} : { scale: 0.95 }}
              onClick={() => handleDateClick(date)}
              disabled={disabled}
              className={`
                p-2 text-sm rounded-lg transition-all duration-200
                ${selected 
                  ? 'bg-pragyavan-gold text-pragyavan-brown font-medium' 
                  : today
                  ? 'bg-pragyavan-brown text-pragyavan-gold font-medium'
                  : disabled
                  ? 'text-warm-gray/30 cursor-not-allowed'
                  : 'text-pragyavan-brown hover:bg-pragyavan-cream'
                }
              `}
            >
              {format(date, 'd')}
            </motion.button>
          );
        })}
      </div>
      
      {/* Footer */}
      <div className="mt-4 pt-4 border-t border-warm-gray/10">
        <p className="text-xs text-warm-gray text-center">
          {selectedDate ? `Selected: ${format(selectedDate, 'EEEE, MMMM d, yyyy')}` : 'Select a date'}
        </p>
      </div>
    </div>
  );
};

export default Calendar;